import { useRef, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import { api } from "../../api";
import Dialog from "../Dialog";
import UptimePanel from "./UptimePanel";
import {
  CONVERSION_EVENTS_MAX,
  DEFAULT_CONVERSION_EVENTS,
  MONEY_PATHS_MAX,
  US_TIME_ZONES,
  conversionEventsFrom,
  isUsTimeZone,
  moneyPathRow,
  moneyPathsFromRows,
  settingsPatch,
  type PathIssue,
  type SiteSettings,
} from "../../web-analytics";

const ISSUE_TEXT: Record<PathIssue, string> = {
  invalid: "Not a page path. Use letters, numbers and slashes, with no spaces.",
  duplicate: "Already listed; it is saved once.",
  limit: `Over the ${MONEY_PATHS_MAX}-page limit. Remove a page to save.`,
};

const DEFAULT_NAMES = DEFAULT_CONVERSION_EVENTS.map((e) => e.name as string);

type Row = { id: number; text: string };

/**
 * A site's settings, ported from Sonar's SiteSettings as a NOLA dialog
 * (Dialog handles the focus trap, Esc and focus return). Only the fields that
 * changed are sent; the API's 400 message shows inline, above the buttons.
 */
export default function SiteSettingsDialog({ site, clientId, onClose, onSaved }: { site: SiteSettings; clientId?: number; onClose: () => void; onSaved: (site: SiteSettings) => void }) {
  const domain = site.domains[0] || "";
  const nextId = useRef(0);
  const row = (text: string): Row => ({ id: nextId.current++, text });

  const [label, setLabel] = useState(site.label ?? "");
  const [zone, setZone] = useState(isUsTimeZone(site.time_zone) ? site.time_zone : "other");
  const [otherZone, setOtherZone] = useState(isUsTimeZone(site.time_zone) ? "" : site.time_zone);
  const [rows, setRows] = useState<Row[]>(() => (site.money_paths.length ? site.money_paths.map(moneyPathRow) : [""]).map(row));
  const [checked, setChecked] = useState(() => new Set(site.conversion_events.filter((e) => DEFAULT_NAMES.includes(e))));
  const [extra, setExtra] = useState(site.conversion_events.filter((e) => !DEFAULT_NAMES.includes(e)).join(", "));
  const [monitor, setMonitor] = useState(site.monitor);
  const [monitorUrl, setMonitorUrl] = useState(site.monitor_url ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastInput = useRef<HTMLInputElement>(null);
  const zoneInput = useRef<HTMLInputElement>(null);

  const money = moneyPathsFromRows(rows.map((r) => r.text));
  const events = conversionEventsFrom(DEFAULT_NAMES.filter((n) => checked.has(n)), extra);
  const eventProblem = events.invalid.length
    ? `Event names use lowercase letters, numbers, - and _ (up to 32): ${events.invalid.join(", ")}`
    : events.overLimit ? `At most ${CONVERSION_EVENTS_MAX} conversion events.` : null;
  const blocked = money.blocking || !!eventProblem;
  const timeZone = zone === "other" ? otherZone : zone;
  // The API's zone check (Intl) answers "Invalid time zone"; that one belongs under its field.
  const zoneError = error && /time zone/i.test(error) ? error : null;

  const addRow = () => {
    setRows((r) => [...r, row("")]);
    // Focus the new row once it exists.
    requestAnimationFrame(() => lastInput.current?.focus());
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    if (blocked || saving) return;
    const patch = settingsPatch(site, {
      label, time_zone: timeZone, money_paths: money.paths, conversion_events: events.events, monitor, monitor_url: monitorUrl,
    });
    if (!Object.keys(patch).length) { onClose(); return; }
    setSaving(true);
    setError(null);
    try {
      const updated = await api<SiteSettings>(`/api/web/sites/${site.id}`, { method: "PATCH", body: JSON.stringify(patch) });
      onSaved(updated);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Could not save the settings";
      setError(message);
      setSaving(false);
      // Save is at the bottom and the zone is at the top: take the reader to it.
      if (/time zone/i.test(message)) requestAnimationFrame(() => zoneInput.current?.focus());
    }
  }

  return (
    <Dialog title="Site settings" onClose={onClose}>
      <form className="operation-form wa-settings" onSubmit={save} noValidate>
        <p className="wa-settings-site">{domain || `Site ${site.id}`}</p>

        <label>
          Label
          <input value={label} maxLength={120} placeholder={domain} onChange={(e) => setLabel(e.target.value)} />
          <span className="wa-hint">Shown in the site picker. Leave blank to use the domain.</span>
        </label>

        <fieldset className="wa-fieldset">
          <legend>Time zone</legend>
          <div className="form-pair">
            <label>
              <span className="sr-only">Time zone</span>
              <select value={zone} onChange={(e) => { setZone(e.target.value); setError(null); }}>
                {US_TIME_ZONES.map((z) => <option key={z.id} value={z.id}>{z.label} ({z.id})</option>)}
                <option value="other">Another zone…</option>
              </select>
            </label>
            {zone === "other" && (
              <label>
                <span className="sr-only">Zone name</span>
                <input ref={zoneInput} value={otherZone} placeholder="Europe/London" spellCheck={false} autoCapitalize="none" aria-invalid={!!zoneError}
                  onChange={(e) => { setOtherZone(e.target.value); setError(null); }} />
              </label>
            )}
          </div>
          {zoneError && <p className="wa-field-error" role="alert">{zoneError}. Use a name such as America/Chicago.</p>}
          <span className="wa-hint">Days and hours on this page are counted in this zone. Changing it re-dates the numbers.</span>
        </fieldset>

        <fieldset className="wa-fieldset">
          <legend>Money pages</legend>
          <span className="wa-hint">Pages that show buying intent, such as a quote or booking page. A page also counts the pages under it: /quote covers /quote/thanks.</span>
          <ul className="wa-paths">
            {rows.map((r, i) => {
              const issue = money.issues[i];
              const id = `wa-path-${r.id}`;
              return (
                <li key={r.id}>
                  <div className={issue && issue !== "duplicate" ? "wa-path-row bad" : "wa-path-row"}>
                    <span className="wa-path-prefix" aria-hidden="true">/</span>
                    <input
                      ref={i === rows.length - 1 ? lastInput : undefined}
                      value={r.text}
                      aria-label={`Money page ${i + 1}`}
                      aria-invalid={issue === "invalid" || issue === "limit"}
                      aria-describedby={issue ? id : undefined}
                      placeholder="quote"
                      spellCheck={false}
                      autoCapitalize="none"
                      onChange={(e) => setRows((all) => all.map((x) => (x.id === r.id ? { ...x, text: e.target.value } : x)))}
                    />
                    <button type="button" className="icon-button" aria-label={`Remove money page ${i + 1}`}
                      onClick={() => setRows((all) => (all.length > 1 ? all.filter((x) => x.id !== r.id) : [row("")]))}>
                      <X aria-hidden="true" />
                    </button>
                  </div>
                  {issue && <p id={id} className={issue === "duplicate" ? "wa-hint" : "wa-field-error"}>{ISSUE_TEXT[issue]}</p>}
                </li>
              );
            })}
          </ul>
          <button type="button" className="button secondary wa-add-path" onClick={addRow} disabled={rows.length >= MONEY_PATHS_MAX}>
            <Plus aria-hidden="true" /> Add a page
          </button>
        </fieldset>

        <fieldset className="wa-fieldset">
          <legend>Conversion events</legend>
          <span className="wa-hint">The events that count as a conversion for attribution and ROAS.</span>
          <div className="wa-events">
            {DEFAULT_CONVERSION_EVENTS.map((ev) => (
              <label key={ev.name} className="checkbox-line">
                <input
                  type="checkbox"
                  checked={checked.has(ev.name)}
                  onChange={(e) => setChecked((s) => { const n = new Set(s); if (e.target.checked) n.add(ev.name); else n.delete(ev.name); return n; })}
                />
                <span>{ev.label} <code>{ev.name}</code></span>
              </label>
            ))}
          </div>
          <label>
            Other events
            <input value={extra} placeholder="quote_request, booking" spellCheck={false} autoCapitalize="none"
              aria-invalid={!!eventProblem} onChange={(e) => setExtra(e.target.value)} />
            {eventProblem ? <span className="wa-field-error">{eventProblem}</span> : <span className="wa-hint">Custom event names the site sends, separated by commas.</span>}
          </label>
        </fieldset>

        <fieldset className="wa-fieldset">
          <legend>Uptime</legend>
          <label className="checkbox-line">
            <input type="checkbox" checked={monitor} onChange={(e) => setMonitor(e.target.checked)} />
            <span>Check the site is up, once an hour</span>
          </label>
          <label>
            Address to check
            <input type="url" value={monitorUrl} disabled={!monitor} placeholder={domain ? `https://${domain}` : "https://"}
              spellCheck={false} autoCapitalize="none" onChange={(e) => setMonitorUrl(e.target.value)} />
            <span className="wa-hint">
              Leave blank to check the home page of the address the tracking script last reported
              {domain ? `, or https://${domain} before any visits` : ""}. A new site is not alerted on until it has passed one check.
            </span>
          </label>
          {clientId !== undefined && <UptimePanel clientId={clientId} siteId={site.id} timeZone={site.time_zone} compact />}
        </fieldset>

        {error && !zoneError && <p className="form-error" role="alert">{error}</p>}

        <div className="operation-actions">
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="button primary" disabled={saving || blocked}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </form>
    </Dialog>
  );
}
